import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import { AutoFixHigh as ResearchIcon } from '@mui/icons-material';

export const pulse = keyframes`
  0% { opacity: 0.7; transform: scale(0.97); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0.7; transform: scale(0.97); }
`;

interface ResearchStatusProps {
  steps?: string[];
  title?: string;
}

/**
 * Enhanced loading state for deep research - Shared between chat components
 */
export const ResearchStatus: React.FC<ResearchStatusProps> = ({ 
  steps = [
    'Deep Researching your request...',
    'Analyzing context and evidence...',
    'Cross-referencing provided information...',
    'Extracting key facts and findings...',
    'Formulating a comprehensive response...',
    'Finalizing analysis...'
  ],
  title = 'Deep Research in progress'
}) => {
  const [textIndex, setTextIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 2.5,
        py: 4,
        px: 2,
        width: '100%',
        maxWidth: 400,
        mx: 'auto'
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress 
          size={64} 
          thickness={3} 
          sx={{ 
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }} 
        />
        <ResearchIcon 
          sx={{ 
            position: 'absolute', 
            fontSize: 32, 
            color: 'primary.main', 
            animation: `${pulse} 2s infinite ease-in-out` 
          }} 
        />
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'primary.main', 
            fontWeight: 600,
            fontSize: '1.1rem',
            mb: 1,
            animation: `${pulse} 2s infinite ease-in-out`
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            minHeight: '1.5em',
            fontStyle: 'italic'
          }}
        >
          {steps[textIndex]}
        </Typography>
      </Box>
      
      {/* Visual progress dots */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {steps.map((_, i) => (
          <Box 
            key={i}
            sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: i === textIndex ? 'primary.main' : 'grey.300',
              transition: 'background-color 0.3s ease'
            }} 
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Shared Markdown styling for chat messages
 */
export const getChatMarkdownStyles = (isUser: boolean) => ({
  '& p': { 
    marginTop: 0,
    marginBottom: '0.75rem',
    '&:last-child': { marginBottom: 0 }
  },
  '& strong': {
    fontWeight: 700,
    color: isUser ? 'inherit' : 'primary.dark'
  },
  '& em': {
    fontStyle: 'italic'
  },
  '& ul, & ol': {
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    paddingLeft: '1.5rem'
  },
  '& li': {
    marginBottom: '0.25rem'
  },
  '& h1, & h2, & h3, & h4': {
    marginTop: '1.25rem',
    marginBottom: '0.75rem',
    fontWeight: 700,
    color: isUser ? 'inherit' : 'primary.dark',
    '&:first-of-type': { marginTop: 0 }
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'primary.light',
    pl: 2,
    py: 0.5,
    my: 1.5,
    bgcolor: 'rgba(25, 118, 210, 0.04)',
    borderRadius: 1
  },
  '& code': {
    backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    padding: '0.125rem 0.25rem',
    borderRadius: '3px',
    fontSize: '0.9em',
    fontFamily: 'monospace'
  }
});
